import React, { useState } from "react";
import { toast } from "sonner";
import {
  Link as LinkIcon, Copy, ExternalLink, Lock, Ticket, Handshake,
  MessageCircle, Users, Sparkles, ShieldCheck,
} from "lucide-react";

/**
 * Central directory of every shareable URL for SSU Workshops.
 * Grouped by purpose so Jacob (and future team) always knows which link
 * to send to which person.
 */
const ORIGIN = typeof window !== "undefined" ? window.location.origin : "";

const SECTIONS = [
  {
    id: "private",
    title: "Private Registration Links",
    subtitle:
      "Do NOT share publicly. These skip normal payment flow — use for people you've already collected money from off-platform (Zelle, cash, invoice, etc.).",
    accent: "#25D366",
    icon: Lock,
    links: [
      {
        label: "Paid-Externally Registration",
        description:
          "Full attendee info form → auto-redirects to WhatsApp group. No Stripe. No ticket picker. Use this for anyone you've already been paid by via Zelle or another method.",
        path: "/register-paid",
        badge: "Zelle / off-platform paid",
      },
    ],
  },
  {
    id: "tickets",
    title: "Ticket Deep-Links (pre-selected tier)",
    subtitle:
      "Send someone a direct link to register + pay for a specific ticket. Opens the standard registration flow with that tier already selected.",
    accent: "#0066FF",
    icon: Ticket,
    links: [
      { label: "Day Pass · Pick Fri/Sat", path: "/register?tier=single-day" },
      { label: "Weekend Pass", path: "/register?tier=day-pass" },
      { label: "Women's Bunk", path: "/register?tier=mens-bunk" },
      {
        label: "Solo Room",
        path: "/register?tier=solo-room",
        badge: "Sold out",
        badgeColor: "#DC2626",
      },
    ],
  },
  {
    id: "sponsors",
    title: "Sponsorship Deep-Links",
    subtitle:
      "Direct link to the sponsor application with a specific tier pre-selected. Great for warm outreach to brands.",
    accent: "#FACC15",
    icon: Handshake,
    links: [
      {
        label: "Title Partner Application",
        path: "/sponsor?tier=title-partner",
        badge: "$4,500 · 1 spot",
      },
      {
        label: "Supporting Partner Application",
        path: "/sponsor?tier=supporting-partner",
        badge: "$1,400 · 2 spots",
      },
      {
        label: "Community Sponsor Application",
        path: "/sponsor?tier=community-sponsor",
        badge: "Product · no cost",
      },
      { label: "Sponsorship Overview (all tiers)", path: "/sponsor" },
    ],
  },
  {
    id: "public",
    title: "Public Pages",
    subtitle: "The main event pages people already know about.",
    accent: "#0A1128",
    icon: Sparkles,
    links: [
      { label: "Landing Page", path: "/" },
      { label: "Registration (open flow, pick your own tier)", path: "/register" },
    ],
  },
  {
    id: "waiver",
    title: "Liability Waiver",
    subtitle:
      "Send this to every confirmed attendee before the workshop. Signed PDFs are emailed to Jacob and stored in the Waivers tab.",
    accent: "#22C55E",
    icon: ShieldCheck,
    links: [
      { label: "Liability Waiver Form", path: "/waiver", badge: "Required · pre-arrival" },
    ],
  },
  {
    id: "community",
    title: "Community & Off-Platform",
    subtitle: "External links you'll share often — kept here so nothing gets lost.",
    accent: "#25D366",
    icon: MessageCircle,
    links: [
      {
        label: "WhatsApp Group (attendee community)",
        external: "https://chat.whatsapp.com/HSRkuBWMRztKlJYytthG6u",
      },
      {
        label: "Skool Community",
        external: "https://www.skool.com/superheroswimuniversity/about",
      },
    ],
  },
  {
    id: "admin",
    title: "Admin Access",
    subtitle: "For you and anyone you trust with backend access.",
    accent: "#6366F1",
    icon: Users,
    links: [
      { label: "Admin Login", path: "/admin/login" },
      { label: "Admin Dashboard", path: "/admin" },
    ],
  },
];


function LinkRow({ link, sectionAccent }) {
  const url = link.external || `${ORIGIN}${link.path}`;
  const [copied, setCopied] = useState(false);

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Link copied");
      setTimeout(() => setCopied(false), 1500);
    } catch (e) {
      toast.error("Copy failed — highlight the URL and copy manually");
    }
  };

  return (
    <div
      className="flex items-start gap-3 p-4 border border-[#E5E7EB] rounded-lg bg-white hover:border-[#0066FF]/40 transition-colors"
      data-testid={`link-row-${link.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <div className="font-semibold text-[#0A1128] text-sm">{link.label}</div>
          {link.badge && (
            <span
              className="text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full"
              style={{
                background: (link.badgeColor || sectionAccent) + "20",
                color: link.badgeColor || sectionAccent,
              }}
            >
              {link.badge}
            </span>
          )}
        </div>
        {link.description && (
          <p className="text-xs text-[#6B7280] mb-2 leading-snug max-w-2xl">{link.description}</p>
        )}
        <div className="font-mono text-[11px] text-[#0066FF] break-all bg-[#F9FAFB] border border-[#E5E7EB] rounded px-2 py-1 select-all">
          {url}
        </div>
      </div>
      <div className="flex flex-col sm:flex-row gap-2 shrink-0">
        <button
          type="button"
          onClick={onCopy}
          data-testid={`copy-${link.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
          className="inline-flex items-center gap-1.5 border border-[#0A1128] text-[#0A1128] hover:bg-[#0A1128] hover:text-white transition-colors text-xs font-bold uppercase tracking-wider px-3 py-2 rounded-md"
        >
          <Copy size={12} /> {copied ? "Copied" : "Copy"}
        </button>
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 bg-[#0066FF] hover:bg-[#0052CC] text-white transition-colors text-xs font-bold uppercase tracking-wider px-3 py-2 rounded-md"
        >
          <ExternalLink size={12} /> Open
        </a>
      </div>
    </div>
  );
}


export default function LinksTab() {
  return (
    <div data-testid="links-tab" className="space-y-8">
      <div>
        <h2 className="font-display-tight text-2xl text-[#0A1128] flex items-center gap-2">
          <LinkIcon size={22} /> Shareable Links
        </h2>
        <p className="text-sm text-[#6B7280] mt-1 max-w-2xl">
          Every URL you'll ever need to send to a person — organized by purpose.
          Copy any link and DM/text/email it directly. Private links are never shown on the public site.
        </p>
      </div>

      {SECTIONS.map((section) => {
        const Icon = section.icon;
        return (
          <section
            key={section.id}
            data-testid={`links-section-${section.id}`}
            className="bg-[#FAFAF7] border border-[#E5E7EB] rounded-xl p-5 md:p-6"
          >
            <div className="flex items-start gap-3 mb-4">
              <div
                className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
                style={{ background: section.accent + "15", color: section.accent }}
              >
                <Icon size={20} />
              </div>
              <div className="flex-1">
                <h3 className="font-display-tight text-lg text-[#0A1128]">{section.title}</h3>
                {section.subtitle && (
                  <p className="text-xs text-[#6B7280] mt-1 leading-snug max-w-2xl">{section.subtitle}</p>
                )}
              </div>
            </div>
            <div className="space-y-2">
              {section.links.map((link) => (
                <LinkRow key={link.label} link={link} sectionAccent={section.accent} />
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
}
